import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons/faSparkles';

import React, { useState, useEffect, useCallback } from 'react';
import classnames from 'classnames';

import { InteractablePopover } from 'ui-components/components/interactable-popover';
import { noop } from 'ui-components/default-props/noop';

import style from './rate-recommendation.module.less';
import { SuggestAlternativeRatePopoverContent } from './suggest-alternative-rate-popover-content';

/**
 * @typedef {object} SavingsObject
 * @property {number} value - The value of the savings.
 * @property {string} code - The currency code of the savings.
 */

/**
 * SuggestAlternativeRatePopover component displays a popover with alternative rate suggestions.
 * @param {object} props - The properties object.
 * @param {SavingsObject} props.savings - The savings object.
 * @param {string} props.alternateServiceName - The name of the alternate service.
 * @param {() => void} props.saveConfiguration - The function to save the configuration.
 * @param {() => void} props.onPopOverOpen - The function to run when icon clicked.
 * @returns {JSX.Element|null} The rendered component or null if alternative rates in order grid are not enabled or there are no savings.
 */
export const SuggestAlternativeRatePopover = ({
  savings = undefined,
  alternateServiceName = undefined,
  saveConfiguration = noop,
  onPopOverOpen = noop,
}) => {
  const [originalSavings, setOriginalSavings] = useState(savings);
  const [isDisabled, setIsDisabled] = useState(false);

  useEffect(() => {
    if (!!savings && savings.value > 0) {
      setOriginalSavings(savings);
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  }, [originalSavings, savings]);
  const onClick = useCallback(() => {
    saveConfiguration();
    setIsDisabled(true);
  }, [saveConfiguration]);

  const createPopoverBody = useCallback(
    ({ close, popoverType }) => {
      return (
        <SuggestAlternativeRatePopoverContent
          alternateServiceName={alternateServiceName}
          appliedSavings={isDisabled}
          onClick={onClick}
          onClose={close}
          originalSavings={originalSavings}
          popoverType={popoverType}
        />
      );
    },
    [alternateServiceName, isDisabled, onClick, originalSavings],
  );

  return (
    <InteractablePopover contentRenderProp={createPopoverBody} onPopOverOpen={onPopOverOpen} preferPlace='bottom'>
      <div
        className={classnames(style['savings-icon'], {
          [style['savings-icon-disabled']]: isDisabled,
          [style['savings-icon-enabled']]: !isDisabled,
        })}
      >
        <FontAwesomeIcon icon={faSparkles} />
      </div>
    </InteractablePopover>
  );
};
